/**
 * Instrument combobox.
 *
 * Lives in the desktop sidebar and, on mobile, in the sticky top bar — one
 * component so the keyboard behaviour and the COMPOSITE/IHSG alias handling
 * cannot drift between the two placements.
 *
 * A plain listbox over the instrument catalogue, no combobox library: the
 * catalogue is a local list and keyboard access is not optional, but neither
 * justifies a dependency.
 */
import { useId, useMemo, useRef, useState } from "react";

import { searchInstruments, type InstrumentOption } from "../domain/instruments.ts";
import { INSTRUMENT_FIXTURE } from "../fixtures/instruments.ts";

interface InstrumentSearchProps {
	onSelect: (code: string) => void;
	/** The catalogue to search. Defaults to the bundled one for offline use. */
	catalogue?: readonly InstrumentOption[];
	/** Where that catalogue came from, so the empty state can be honest. */
	catalogueOrigin?: "api" | "fixture";
	/** Rendered above the input on the rail; the top bar labels it invisibly. */
	label?: string;
	labelHidden?: boolean;
	className?: string;
}

export function InstrumentSearch({
	onSelect,
	catalogue = INSTRUMENT_FIXTURE,
	catalogueOrigin = "fixture",
	label = "Instrument",
	labelHidden = false,
	className,
}: InstrumentSearchProps) {
	const [query, setQuery] = useState("");
	const [highlight, setHighlight] = useState(0);
	const listboxId = useId();
	const inputRef = useRef<HTMLInputElement>(null);

	const matches = useMemo(() => searchInstruments(query, catalogue), [query, catalogue]);

	function choose(option: InstrumentOption | undefined) {
		if (!option) return;
		// The canonical code goes out; the caller resolves aliases again on its
		// side, so IHSG and COMPOSITE cannot become two different requests.
		onSelect(option.code);
		setQuery("");
		setHighlight(0);
		inputRef.current?.blur();
	}

	function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setHighlight((current) => Math.min(current + 1, matches.length - 1));
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlight((current) => Math.max(current - 1, 0));
		} else if (event.key === "Enter") {
			event.preventDefault();
			choose(matches[highlight]);
		} else if (event.key === "Escape") {
			// Clear the query rather than bubbling: Escape in an open combobox
			// should not also close the drawer behind it.
			event.stopPropagation();
			setQuery("");
		}
	}

	return (
		<div className={className ? `instrument-search ${className}` : "instrument-search"}>
			<label className={labelHidden ? "visually-hidden" : "sidebar__label"} htmlFor={`${listboxId}-input`}>
				{label}
				{/* Visible without typing: an offline list is a smaller set than
				    the market, and the reader should know before they search. */}
				{catalogueOrigin === "fixture" ? <span className="sidebar__offline"> · offline list</span> : null}
			</label>
			<input
				id={`${listboxId}-input`}
				ref={inputRef}
				className="search"
				type="search"
				placeholder="Search IHSG, BBRI, ANTM…"
				value={query}
				autoComplete="off"
				role="combobox"
				aria-expanded={query.length > 0}
				aria-controls={listboxId}
				aria-activedescendant={query ? `${listboxId}-${highlight}` : undefined}
				onChange={(event) => {
					setQuery(event.target.value);
					setHighlight(0);
				}}
				onKeyDown={onKeyDown}
			/>
			{query ? (
				<ul className="listbox" id={listboxId} role="listbox" aria-label="Matching instruments" data-origin={catalogueOrigin}>
					{matches.length === 0 ? (
						<li className="listbox__empty">
							{catalogueOrigin === "api"
								? "No match in the instrument list."
								: "No match in the offline instrument list."}
						</li>
					) : null}
					{matches.map((option, index) => (
						<li
							key={option.code}
							id={`${listboxId}-${index}`}
							role="option"
							aria-selected={index === highlight}
							className="listbox__option"
							data-highlighted={index === highlight}
							onMouseEnter={() => setHighlight(index)}
							onMouseDown={(event) => {
								event.preventDefault();
								choose(option);
							}}
						>
							<span className="listbox__code">{option.code.toUpperCase()}</span>
							<span className="listbox__name">{option.name}</span>
							<span className="listbox__sector">{option.sector}</span>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}
